import React, { useState, useEffect } from "react";
import { useJwt } from "react-jwt";
import { useLocation } from 'react-router-dom';

import { authService } from "../services/auth";
import WeaveLoader from "./common/WeaveLoader";

const AuthLoader = () => (
  <div className="flex min-h-screen items-center justify-center bg-theme-main">
    <WeaveLoader size={72} />
  </div>
);

// high level component, wraps most of app
// passes auth params to children components
// upon state changes, re-assesses expiration of access token
const Authentication = ({children}) => {
  const location = useLocation();
  // state
  const [ accessToken, setAccessToken ] = useState(localStorage.getItem('accessToken'));
  const [ isRefreshing, setIsRefreshing ] = useState(localStorage.getItem('refreshingToken'));
  const [ userConfigReady, setUserConfigReady ] = useState(!localStorage.getItem('accessToken'));
  const [ currentUser, setCurrentUser ] = useState(() =>
    JSON.parse(localStorage.getItem('currentUser') || 'null'),
  );
  const { isExpired } = useJwt(localStorage.getItem('accessToken'));

  // effects
  useEffect(() => {
    if (accessToken && isExpired){
      authService.refreshToken((newToken) => {
        setAccessToken(newToken)
      }, () => {
        // refresh token is expired; send to login with return path
        const next = encodeURIComponent(window.location.pathname + window.location.search);
        window.location = `/login/?next=${next}&autologgedout=1`;
      });
    }
  }, [
    accessToken,
    isExpired,
  ]); // If current token is expired, go refresh it

  useEffect(() => {
    if (accessToken && !isExpired){
      setUserConfigReady(false);
      authService.fetchUser(
        (response) => {
          setCurrentUser(response.data);
          setUserConfigReady(true);
        },
        () => {
          setUserConfigReady(true);
        },
      );
    } else if (!accessToken) {
      setUserConfigReady(true);
      setCurrentUser(null);
    }
  }, [
    accessToken,
    isExpired,
  ]); // Refetches user config when a new access token is set

  if (accessToken && isExpired) {
    return <AuthLoader />;
  }

  if (accessToken && !isExpired && !userConfigReady) {
    return <AuthLoader />;
  }

  // clean slate of props we return to all components
  var authState = {
    'authenticated': false,
    'accessToken': null,
    'refreshToken': null,
    'refreshingToken': false,
    'currentUser': null,
    'setCurrentUser': setCurrentUser,
    'location': location,
  }

  // determine returning properties based on state of current token
  if (accessToken && !isExpired){
    const storedUser = JSON.parse(localStorage.getItem('currentUser') || 'null');
    const effectiveUser = currentUser ?? storedUser;
    // we're ready to rock!
    authState = Object.assign(authState, {
      'authenticated': true,
      'accessToken': localStorage.getItem('accessToken'),
      'refreshToken': localStorage.getItem('refreshToken'),
      'currentUser': effectiveUser,
    })
  } else if (accessToken && isExpired) {
    // it's expired. the effect in this file will get a new one
    authState = Object.assign(authState, {
      'refreshingToken': true,
    })
  } else {
    // not authenticated. this is an OK state depending on which route it is
    // screens like /account/ + /tests/ will kick out to login,
    // while screens like /login/ + /register/ are fine 
  }

  return (
    <React.Fragment>
      {children({
        ...authState,
      })}
    </React.Fragment>
  )
};

export default Authentication;
